import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Download, ExternalLink } from 'lucide-react';

const BusinessPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="bg-[#1a1a1a] py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Our Business</h1>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Comprehensive grain handling services tailored to meet the needs of growers and traders across regional Victoria.
            </p>
          </div>
        </section>

        {/* Storage and Handling Section */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4 md:mb-0">Storage and Handling</h2>
              <div className="text-center md:text-right">
                <p className="text-4xl font-bold text-yellow-500">50,000+ MT</p>
                <p className="text-gray-600">Total Storage Capacity</p>
              </div>
            </div>

            {/* Images Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <img
                src="https://customer-assets.emergentagent.com/job_vic-storage-depot/artifacts/8tecysxv_S%26H%20Pic.JPG"
                alt="Grain outloading operations at Wycheproof Grain Handling"
                className="w-full h-48 object-cover rounded-lg"
              />
              <img
                src="https://customer-assets.emergentagent.com/job_vic-storage-depot/artifacts/fjm9prmi_Crop%20Pic.jpg"
                alt="Wheat crop ready for harvest"
                className="w-full h-48 object-cover rounded-lg"
              />
              <img
                src="https://customer-assets.emergentagent.com/job_vic-storage-depot/artifacts/eo16c3po_Bunker%20pic.jpeg"
                alt="Bunker storage operations at Wycheproof Grain Handling"
                className="w-full h-48 object-cover rounded-lg"
              />
            </div>

            {/* Content */}
            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                At <strong>Wycheproof Grain Handling</strong>, we take pride in delivering secure, efficient, and professional grain storage and handling. With modern facilities and a team of experienced specialists, we ensure your harvest is stored safely, maintained at top quality, and ready for timely distribution. Trust us to handle your grain with care, precision and reliability.
              </p>
              <p>
                Wycheproof Grain Handling offers extensive storage capacity with five large bunkers and over ten silos to protect your harvest.
              </p>
              <p>
                Our state-of-the-art machinery ensures grain is unloaded, moved, and stored quickly and safely — keeping quality high and turnaround times low.
              </p>
              <p>
                We're proud to deliver reliable service that local growers can count on, season after season.
              </p>
            </div>

            {/* Agreement Download */}
            <div className="mt-10 bg-[#faf8f5] rounded-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Storage & Handling Agreement 2025/26</h3>
              <p className="text-gray-700 mb-4">
                Download our comprehensive storage and handling agreement for the 2025/26 harvest season.
              </p>
              <a
                href="https://customer-assets.emergentagent.com/job_aussie-grain/artifacts/2ltmcd6j_5piol1vx_Wyche%20Grain%20Agreement%202025-26%20season.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-yellow-500 text-gray-900 font-semibold rounded-md hover:bg-yellow-400 transition-all duration-300"
              >
                <Download size={18} />
                Download Agreement (PDF)
              </a>
              <p className="text-sm text-gray-500 mt-3">
                Agreement period: November 1, 2025 - October 31, 2026
              </p>
            </div>
          </div>
        </section>

        {/* Container Packing Section */}
        <section className="py-16 bg-[#faf8f5]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Container Packing</h2>

            {/* Images Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <img
                src="https://customer-assets.emergentagent.com/job_vic-storage-depot/artifacts/yx113l4k_Reach%20Stacker.jpg"
                alt="Reach stacker loading containers at Wycheproof Grain Handling"
                className="w-full h-48 object-cover rounded-lg"
              />
              <img
                src="https://customer-assets.emergentagent.com/job_vic-storage-depot/artifacts/lbndjhm6_Bunkers%20night.jpg"
                alt="Grain bunkers and silos at sunset at Wycheproof Grain Handling"
                className="w-full h-48 object-cover rounded-lg"
              />
              <img
                src="https://customer-assets.emergentagent.com/job_vic-storage-depot/artifacts/rspy271w_Truck%20unloading.JPG"
                alt="Truck unloading operations at Wycheproof Grain Handling"
                className="w-full h-48 object-cover rounded-lg"
              />
            </div>

            {/* Content */}
            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                Our brand-new container packing facility in Wycheproof is built for precision, speed, and reliability. Equipped with modern machinery and expert operators, we ensure every container is packed efficiently, safely, and to export standards — helping growers access global markets with confidence.
              </p>
              <p>
                Purpose-built for performance, the facility features advanced equipment and streamlined processes to maximise efficiency and maintain product integrity. We can hold and handle several different commodities, complete full fumigation on-site, and are fully registered with GACC for export to China.
              </p>
              <p>
                Setting a new benchmark for precision packing and dependable export logistics, our Wycheproof facility delivers quality and consistency from paddock to port.
              </p>
            </div>

            {/* Agreement Download */}
            <div className="mt-10 bg-white rounded-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Container Packing Agreement 2025/26</h3>
              <p className="text-gray-700 mb-4">
                Download our container packing agreement for the 2025/26 season.
              </p>
              <a
                href="https://customer-assets.emergentagent.com/job_aussie-grain/artifacts/y24n6qgt_efzwqprw_Wyche%20Grain%20Packing%20Agreement%2025-26%20season.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-yellow-500 text-gray-900 font-semibold rounded-md hover:bg-yellow-400 transition-all duration-300"
              >
                <Download size={18} />
                Download Agreement (PDF)
              </a>
              <p className="text-sm text-gray-500 mt-3">
                Agreement period: 2025/26 season
              </p>
            </div>
          </div>
        </section>

        {/* Fertiliser Outloading Section */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Fertiliser Outloading</h2>

            {/* Images Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <img
                src="https://customer-assets.emergentagent.com/job_vic-storage-depot/artifacts/c0d7j2mf_FERT%20Pic.JPG"
                alt="Grain storage silos at Wycheproof Grain Handling"
                className="w-full h-48 object-cover rounded-lg"
              />
              <img
                src="https://images.unsplash.com/photo-1710223221719-6251cb1b5c5b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxmZXJ0aWxpemVyJTIwcGVsbGV0c3xlbnwwfHx8fDE3NjMwODIxMjJ8MA&ixlib=rb-4.1.0&q=85"
                alt="UREA fertiliser pellets"
                className="w-full h-48 object-cover rounded-lg"
              />
              <img
                src="https://customer-assets.emergentagent.com/job_vic-storage-depot/artifacts/k9i772wf_FERT%20Pic%202.JPG"
                alt="Truck operations at sunset at Wycheproof Grain Handling"
                className="w-full h-48 object-cover rounded-lg"
              />
            </div>

            {/* Content */}
            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                We are a proud agent for Nutrien Ag and outload their goods to our valuable growers in the region. From outturn to delivery, we take pride in offering secure storage and dependable handling for fertiliser. With experienced staff and a focus on accuracy and safety, we make sure your product is ready when and where you need it.
              </p>
            </div>

            {/* Partner Section */}
            <div className="mt-10 bg-[#faf8f5] rounded-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Partner: Nutrien Ag Solutions</h3>
              <p className="text-gray-700 mb-4">
                Purchase your fertiliser from Nutrien Ag Solutions, then bring your paperwork to us for collection or delivery.
              </p>
              <a
                href="https://www.nutrienagsolutions.com.au/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 border-2 border-yellow-500 text-yellow-600 font-semibold rounded-md hover:bg-yellow-500 hover:text-gray-900 transition-all duration-300"
              >
                <ExternalLink size={18} />
                Visit Nutrien Ag Solutions
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default BusinessPage;
